import dayjs from 'dayjs'
import queryString from 'query-string'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Card } from '@mui/material'
import PageUrls from '../../../../constants/page-urls'
import useAxios from '../../../../hooks/useAxios'
import { IMerchant } from '../../../../types/merchant'
import DailySalesByDistributionReportFilter, {
  IByDistributionFilterMapped,
} from './components/DailySalesByDistributionReportFilter'
import DailySalesByDistributionReportTable, {
  IByDistributionReportRow,
} from './components/DailySalesByDistributionReportTable'

const DailySalesByDistributionReport = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)

  const [searchFilter, setSearchFilter] = useState<IByDistributionFilterMapped>({
    categoryObjectIds: queryStr.categoryObjectIds?.length
      ? String(queryStr.categoryObjectIds).split(',')
      : undefined,
    productObjectIds: queryStr.productObjectIds?.length
      ? String(queryStr.productObjectIds).split(',')
      : undefined,
    productGroups: queryStr.productGroups?.length
      ? String(queryStr.productGroups).split(',')
      : undefined,
    distributionObjectIds: queryStr.distributionObjectIds?.length
      ? String(queryStr.distributionObjectIds).split(',')
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().subtract(30, 'days').format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).startOf('day').format()
      : dayjs().format(),
    page: queryStr.page ? Number(queryStr.page) : 1,
    perPage: queryStr.perPage ? Number(queryStr.perPage) : 15,
  })
  const [filter, setFilter] = useState<IByDistributionFilterMapped>({
    categoryObjectIds: queryStr.categoryObjectIds?.length
      ? String(queryStr.categoryObjectIds).split(',')
      : undefined,
    productObjectIds: queryStr.productObjectIds?.length
      ? String(queryStr.productObjectIds).split(',')
      : undefined,
    productGroups: queryStr.productGroups?.length
      ? String(queryStr.productGroups).split(',')
      : undefined,
    distributionObjectIds: queryStr.distributionObjectIds?.length
      ? String(queryStr.distributionObjectIds).split(',')
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : dayjs().subtract(30, 'days').format(),
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).startOf('day').format()
      : dayjs().format(),
    page: queryStr.page ? Number(queryStr.page) : 1,
    perPage: queryStr.perPage ? Number(queryStr.perPage) : 15,
  })

  const onFilterChange = useCallback(
    (newFilter: IByDistributionFilterMapped) => {
      setFilter(newFilter)
    },
    [],
  )

  const handleSearch = (newFilter: IByDistributionFilterMapped) => {
    const withPage = { ...newFilter, page: 1 }
    setFilter(withPage)
    setSearchFilter(withPage)
    const newQuery = queryString.stringify({
      ...newFilter,
      page: 1,
    })
    navigate(`${PageUrls.Report.DailySales}?${newQuery}`, {
      replace: true,
    })
  }

  const handlePageChange = (page: number, perPage: number) => {
    const next = { ...searchFilter, page, perPage }
    setFilter(next)
    setSearchFilter(next)
    const newQuery = queryString.stringify({ ...searchFilter, page, perPage })
    navigate(`${PageUrls.Report.DailySales}?${newQuery}`, {
      replace: true,
    })
  }

  useEffect(() => {
    handleSearch(filter)
  }, [])

  const byDistributionReports = useAxios<{
    records: IByDistributionReportRow[]
    total: number
  }>({
    method: 'POST',
    url: '/report/merchant-daily-reports-by-distribution',
    data: {
      startDate: searchFilter.startDate,
      endDate: searchFilter.endDate,
      products: searchFilter.productObjectIds,
      productGroups: searchFilter.productGroups,
      categories: searchFilter.categoryObjectIds,
      distribution: searchFilter.distributionObjectIds,
      page: searchFilter.page ?? 1,
      perPage: searchFilter.perPage ?? 15,
    },
  })

  const merchant = useAxios<IMerchant>({
    method: 'GET',
    url: '/merchant/me',
  })

  return (
    <Card sx={{ padding: 2 }}>
      <DailySalesByDistributionReportFilter
        filter={filter}
        onFilterChange={onFilterChange}
        onSearch={handleSearch}
      />
      <DailySalesByDistributionReportTable
        data={byDistributionReports.data?.records ?? []}
        total={byDistributionReports.data?.total ?? 0}
        isLoading={byDistributionReports.isLoading}
        merchant={merchant.data}
        filter={filter}
        onPageChange={handlePageChange}
      />
    </Card>
  )
}

export default DailySalesByDistributionReport
